import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDataTable, type DataTableFilter, type DataTableRefs } from '@odo/shared-ui';
import { usePractices, useInfinitePractices, usePracticeFilters } from '../../hooks/useAccounts';
import { usePageHeader } from '../../providers/PageHeaderProvider';
import { usePageTitle } from '../../hooks/usePageTitle';
import { DATA_TABLE_ROW_HEIGHT } from '../../hooks/useViewportPageSize';
import { useViewportTableLimit } from '../../hooks/useViewportTableLimit';
import type { PracticesSortBy, SortOrder } from '../../services/accounts';
import type { PracticeRiskStatus } from '../../services/practiceRisk';
import { PRACTICE_RISK_STATUS_LABELS } from '../../components/shared/practiceRisk';
import {
    PracticeStatCards,
    type PracticeStat
} from '../../components/accounts/practices/PracticeStatCards';
import { PracticesTable } from '../../components/accounts/practices/PracticesTable';

const SEARCH_DEBOUNCE_MS = 300;
// Starting limit only: the paged fetch waits for the viewport measurement.
const VIEWPORT_DEFAULT_PAGE_SIZE = 20;

// Pre-migration keys, kept as-is (same two bare-string keys, same format) -
// see the module doc comment below for why sort/order stay hand-rolled
// instead of going through useDataTable's `storageKey`.
const SORT_STORAGE_KEY = 'fv_accounts_practicesSort';
const ORDER_STORAGE_KEY = 'fv_accounts_practicesOrder';
const STAT_STORAGE_KEY = 'fv_accounts_practicesStat';

type PracticeFilters = {
    accountType: string[];
    state: string[];
    city: string[];
    hasOpenCalls: boolean;
    // string[], like every sibling filter here - narrowed to
    // PracticeRiskStatus[] only where it's handed to the fetch params below.
    riskStatus: string[];
};

const SORT_VALUES: readonly PracticesSortBy[] = ['name', 'doctor', 'date', 'patients'];
const ORDER_VALUES: readonly SortOrder[] = ['asc', 'desc'];

// A closed, ODO-owned set (unlike accountType/state/city, which come from
// GET /practices/filters) - hardcoded here rather than sourced from that
// endpoint, so the filter renders even on a response that doesn't (yet)
// carry it.
const RISK_STATUS_FILTER_OPTIONS: { value: PracticeRiskStatus; label: string }[] = [
    { value: 'normal', label: PRACTICE_RISK_STATUS_LABELS.normal },
    { value: 'warning', label: PRACTICE_RISK_STATUS_LABELS.warning },
    { value: 'at_risk', label: PRACTICE_RISK_STATUS_LABELS.at_risk }
];

// The endpoint validates sort/order as enums, so a non-member from storage has
// to fall back rather than reach the wire; stale entries may be JSON-quoted.
function loadSession<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
    try {
        const raw = sessionStorage.getItem(key);
        if (raw === null) return fallback;
        const value = raw.startsWith('"') ? String(JSON.parse(raw)) : raw;
        return allowed.includes(value as T) ? (value as T) : fallback;
    } catch {
        return fallback;
    }
}

function saveSession(key: string, value: string): void {
    try {
        sessionStorage.setItem(key, value);
    } catch {
        /* Persistence is a convenience; a quota error is not an app error. */
    }
}

/**
 * One-time format migration for the "Unresolved calls" stat toggle, run
 * before `useDataTable` reads storage. `filterStorageKeys` (see below) writes
 * this key as JSON (`useDataTable`'s `readStored`/`writeStored`), but the
 * pre-migration value was a bare, un-quoted string ('calls' | 'all') - not
 * valid JSON, so `JSON.parse` would throw and silently fall back to the
 * default. This rewrites it to a JSON boolean once so the toggle survives the
 * migration instead of resetting to "All practices".
 */
function migrateLegacyStatFilter(): void {
    try {
        const raw = sessionStorage.getItem(STAT_STORAGE_KEY);
        if (raw === null || raw === 'true' || raw === 'false') return;
        sessionStorage.setItem(STAT_STORAGE_KEY, JSON.stringify(raw === 'calls'));
    } catch {
        /* Best-effort migration; a storage error just skips it. */
    }
}

export function PracticesList() {
    usePageTitle('Practices');
    const { setTitle, setSubtitle } = usePageHeader();
    const navigate = useNavigate();

    migrateLegacyStatFilter();

    // Sort/order are deliberately NOT wired through useDataTable's
    // `storageKey`: that mechanism bundles whatever filters aren't given
    // their own `filterStorageKeys` entry into the same persisted blob, which
    // would start persisting accountType/state/city too - filters that were
    // never persisted before this migration. Keeping them hand-rolled here,
    // under their original two keys and format, reproduces the pre-migration
    // behaviour exactly with no new persistence and no one-time reset.
    // `hasOpenCalls` has no such spillover risk - it's one named filter with
    // its own key - so it uses `filterStorageKeys` directly, same as
    // InvoicesContent's status/practiceId filters.
    const table = useDataTable<PracticesSortBy, PracticeFilters>({
        defaultSort: {
            by: loadSession(SORT_STORAGE_KEY, SORT_VALUES, 'name'),
            order: loadSession(ORDER_STORAGE_KEY, ORDER_VALUES, 'asc')
        },
        defaultLimit: VIEWPORT_DEFAULT_PAGE_SIZE,
        defaultFilters: {
            accountType: [],
            state: [],
            city: [],
            hasOpenCalls: false,
            riskStatus: []
        },
        filterStorageKeys: { hasOpenCalls: STAT_STORAGE_KEY }
    });
    const {
        accountType: accountTypeFilter,
        state: stateFilter,
        city: cityFilter,
        hasOpenCalls,
        riskStatus: riskStatusFilter
    } = table.filters;
    const isInfinite = table.mode === 'infinite';

    useEffect(() => {
        saveSession(SORT_STORAGE_KEY, table.sort);
    }, [table.sort]);
    useEffect(() => {
        saveSession(ORDER_STORAGE_KEY, table.order);
    }, [table.order]);

    const [searchInput, setSearchInput] = useState('');
    const { setSearch: commitSearch } = table;
    useEffect(() => {
        const handle = setTimeout(() => commitSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(handle);
    }, [searchInput, commitSearch]);

    const {
        isReady: pageSizeReady,
        tableCardRef,
        tableHeaderRef,
        tableBodyRef,
        paginationRef
    } = useViewportTableLimit(table, { rowHeight: DATA_TABLE_ROW_HEIGHT });

    const { page, limit, sort, order } = table.params;
    const sharedPracticeParams = {
        limit,
        q: table.search || undefined,
        sort,
        order,
        hasOpenCalls: hasOpenCalls || undefined,
        accountType: accountTypeFilter.length > 0 ? accountTypeFilter : undefined,
        state: stateFilter.length > 0 ? stateFilter : undefined,
        city: cityFilter.length > 0 ? cityFilter : undefined,
        riskStatus:
            riskStatusFilter.length > 0 ? (riskStatusFilter as PracticeRiskStatus[]) : undefined
    };
    // Both hooks are called unconditionally (React's rules of hooks) and only
    // one is ever `enabled` - same split as InvoicesContent's paged/infinite pair.
    const paged = usePractices({
        ...sharedPracticeParams,
        page,
        enabled: !isInfinite && pageSizeReady
    });
    const infinite = useInfinitePractices({ ...sharedPracticeParams, enabled: isInfinite });

    const practices = isInfinite ? infinite.practices : paged.practices;
    const meta = isInfinite ? infinite.meta : paged.meta;
    const isLoading = isInfinite ? infinite.isLoading : paged.isLoading;

    const { filterOptions } = usePracticeFilters();

    const totalPages = Math.max(1, Math.ceil(meta.total / limit));

    useEffect(() => {
        setTitle('Practices');
        setSubtitle(`${meta.total} total`);
        return () => {
            setTitle(null);
            setSubtitle(null);
        };
    }, [meta.total, setTitle, setSubtitle]);

    const openPractice = (id: string) => {
        navigate(`/accounts/practices/${id}`);
    };
    const goToNewPractice = () => navigate('/accounts/practices/new');

    // Unresolved calls is the only clickable stat - toggles off back to 'all'
    // when it's already active. All practices always just resets to 'all'.
    const activeStat: PracticeStat = hasOpenCalls ? 'calls' : 'all';
    const handleStatChange = (stat: PracticeStat) => {
        table.setFilter('hasOpenCalls', stat === 'calls' ? !hasOpenCalls : false);
    };

    const hasFilters =
        accountTypeFilter.length > 0 ||
        stateFilter.length > 0 ||
        cityFilter.length > 0 ||
        riskStatusFilter.length > 0;
    const clearFilters = () => {
        setSearchInput('');
        table.setFilter('accountType', []);
        table.setFilter('state', []);
        table.setFilter('city', []);
        table.setFilter('riskStatus', []);
    };

    const filters: DataTableFilter[] = [
        {
            key: 'accountType',
            label: 'Account Type',
            multiple: true,
            options: filterOptions.accountTypes,
            value: accountTypeFilter,
            onChange: (value) => table.setFilter('accountType', value),
            align: 'center'
        },
        {
            key: 'riskStatus',
            label: 'Risk',
            multiple: true,
            options: RISK_STATUS_FILTER_OPTIONS,
            value: riskStatusFilter,
            onChange: (value) => table.setFilter('riskStatus', value),
            align: 'center'
        },
        {
            key: 'location',
            label: 'Location',
            align: 'center',
            sections: [
                {
                    key: 'states',
                    label: 'States',
                    multiple: true,
                    options: filterOptions.states,
                    value: stateFilter,
                    onChange: (value) => table.setFilter('state', value)
                },
                {
                    key: 'cities',
                    label: 'Cities',
                    multiple: true,
                    options: filterOptions.cities,
                    value: cityFilter,
                    onChange: (value) => table.setFilter('city', value)
                }
            ]
        }
    ];

    const refs: DataTableRefs = {
        card: tableCardRef,
        head: tableHeaderRef,
        body: tableBodyRef,
        pagination: paginationRef
    };

    return (
        <div className="flex flex-col h-full overflow-hidden bg-gray-100">
            <div className="px-8 pt-4 shrink-0">
                <PracticeStatCards
                    total={meta.total}
                    shownCount={practices.length}
                    withOpenCalls={meta.summary.withOpenCalls}
                    noActivity90d={meta.summary.noActivity90d}
                    activeStat={activeStat}
                    onChange={handleStatChange}
                />
            </div>

            <PracticesTable
                practices={practices}
                isLoading={isLoading}
                search={table.search}
                hasFilters={hasFilters}
                onClearFilters={clearFilters}
                onOpenPractice={openPractice}
                onAddPractice={goToNewPractice}
                sort={{ by: table.sort, order: table.order, onSort: table.toggleSort }}
                mode={table.mode}
                onModeChange={table.setMode}
                pagination={{
                    page: table.page,
                    totalPages,
                    total: meta.total,
                    onPage: table.setPage
                }}
                onLoadMore={infinite.fetchNextPage}
                hasMore={infinite.hasNextPage}
                isLoadingMore={infinite.isFetchingNextPage}
                searchConfig={{
                    value: searchInput,
                    onChange: setSearchInput,
                    placeholder: 'Search by name or phone number'
                }}
                filters={filters}
                refs={refs}
            />
        </div>
    );
}
